import React from 'react';
import { Link } from 'react-router-dom';
import QuoteWidget from '@/components/QuoteWidget';
import { Shield, CheckCircle2, ArrowRight, Clock, DollarSign, Users, Home } from 'lucide-react';

const TermLifePage = () => {
  const benefits = [
    { icon: DollarSign, title: "High coverage at low cost", description: "Get substantial protection without breaking the budget" },
    { icon: Clock, title: "Fixed premium payments", description: "Lock in rates for the entire term period" },
    { icon: Shield, title: "Flexible terms", description: "Choose 10, 15, 20, 25, or 30-year terms" },
    { icon: Users, title: "Income replacement", description: "Protect your family's lifestyle if you're gone" }
  ];

  const idealFor = [
    "Young families with growing expenses",
    "Mortgage and debt protection",
    "Income replacement needs",
    "Business continuity planning",
    "Supplementing other coverage"
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="term-life-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <Link to="/services" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
                ← Back to Services
              </Link>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Term Life Insurance
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                Affordable Protection When It Matters Most
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Term life insurance provides straightforward protection for a specific period of time, offering high coverage amounts at affordable premiums. It's the most cost-effective way to ensure your family is protected.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {benefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{benefit.title}</p>
                        <p className="text-slate-500 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Ideal For</h2>
              <ul className="space-y-4">
                {idealFor.map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">How It Works</h2>
              <div className="space-y-4 text-slate-400">
                <p>You pay a fixed premium for a set period (the "term"). If you pass away during that term, your beneficiaries receive the death benefit tax-free.</p>
                <p>If you outlive the term, coverage ends—but many policies offer conversion options to permanent insurance without a new medical exam.</p>
                <p>Our advisors help clients compare policies from leading insurance providers to find the best fit for their budget and needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Get Your Term Life Quote</h2>
          <p className="text-slate-400 mb-8">Compare rates from top carriers in minutes.</p>
          <Link
            to="/compare"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Compare Quotes Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TermLifePage;
